import React, {ReactElement, useState} from 'react';
import {Button, makeStyles, useMediaQuery} from '@material-ui/core';

// styles
import './VaccineCardStyles.scss';
import {useTranslation} from 'react-i18next';
import LabelValue from '../LabelValue/LabelValue';
import {APPOINTMENT_TYPES, AppointmentStatus} from '@shared/constants/Mapping';
import HelperUtils from '@shared/utils/HelperUtils';
import {
  cancelScheduledAppointment,
  downloadVaccineRecord
} from '@shared/services/AppointmentSchedulingService';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useDispatch, useSelector} from 'react-redux';
import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import {
  _setAppointmentPatientId,
  _setUpdateScheduledAppointment
} from '@shared/store/actions/PaymentActions';
import {useHistory, useParams} from 'react-router-dom';
import NavRoutes from 'src/constants/route/NavRoutes';
import DropDownFieldValue from '@components/system/DropDownFieldValue/DropDownFieldValue';
import ConfirmationDialog from '@components/system/ConfirmationDialog/ConfirmationDialog';
import {APP_PRIMARY_COLOR, DEFAULT_RED_COLOR} from 'src/style/variable';

const VaccineCard = (props: {
  appointmentObj: any;
  showPatientName?: boolean;
  clinicZone?: string;
  clinicId: string | number;
  onAppointmentCancelled?: () => void;
}): ReactElement => {
  const {
    appointmentObj,
    showPatientName = true,
    clinicZone = 'America/Chicago',
    clinicId,
    onAppointmentCancelled
  } = props;
  const {t} = useTranslation();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const formattedAppointmentDate = appointmentsDateTimeFormat(
    appointmentObj.appointment_at,
    clinicZone
  );
  const useStyles = makeStyles({
    LinkStyle: {
      whiteSpace: 'nowrap',
      lineHeight: '1.25rem',
      cursor: 'pointer !important',
      color: APP_PRIMARY_COLOR,
      textDecoration: 'underline',
      display: 'flex',
      justifyContent: 'flex-end',
      margin: '0 0 25px 0 !important'
    },
    warningAndButtonsSection: {
      display: 'flex',
      flexDirection: 'column'
    },
    extraLabelStyles: {
      display: 'flex',
      flexDirection: 'column'
    },
    warningtText: {
      color: DEFAULT_RED_COLOR,
      top: 0,
      bottom: 0,
      margin: '10px 0 10px 0'
    }
  });

  const classes = useStyles();
  const patientDetails = useSelector(
    (state: any) => state.payments.patientPaymentDetailsId
  );
  const history = useHistory();

  const dispatch = useDispatch();
  const {token} = useParams<{token: string}>();

  const navigateToAppointmentScheduling = (appointmentData: any): void => {
    dispatch(
      _setAppointmentPatientId({
        patientId: patientDetails.id,
        clinicId: patientDetails?.clinic?.id
      })
    );

    // Dispatch the action when clicked on the edit link for the upcoming appointments
    dispatch(
      _setUpdateScheduledAppointment({
        updateAppointment: true,
        appointmentId: appointmentData?.id,
        patientFirstName: appointmentData?.patient?.first_name,
        patientLastName: appointmentData?.patient?.last_name,
        patientMiddleName: appointmentData?.patient?.middle_name,
        appointmentAt: appointmentData?.appointment_at,
        vaccineId: appointmentData?.vaccine_id,
        patientId: appointmentData?.patient_id,
        createdBy: appointmentData?.created_by,
        vaccineName: appointmentData?.vaccine?.proprietary_name,
        duration: appointmentData?.duration,
        vaccine: appointmentData?.vaccine,
        appointment: appointmentData?.appointment,
        provider: appointmentData?.provider
      })
    );

    // Change the routing when clicked on edit appointment link
    history.push(
      `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${token}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.SCHEDULE_APPOINTMENT}`,
      {
        fromPaymentPortal: true
      }
    );
  };

  const shouldShowVaccine = (): boolean => {
    return !!appointmentObj?.vaccine?.proprietary_name;
  };

  const shouldShowAppointmentType = (): boolean => {
    return !!(
      appointmentObj?.appointment?.label &&
      appointmentObj?.appointment?.label?.toLowerCase() !==
        APPOINTMENT_TYPES.VACCINE.toLowerCase()
    );
  };

  const isVaccineAdministered =
    appointmentObj.status === AppointmentStatus.COMPLETED_PCL;

  const isVaccineScheduled =
    appointmentObj.status === AppointmentStatus.SCHEDULED_PCL;

  const getButtonLabel = (status: string) => {
    switch (status) {
      case AppointmentStatus.CANCELLED_PCL:
        return t(
          'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.VACCINE_CARD.CANCELLED'
        );
      case AppointmentStatus.COMPLETED_PCL:
        return t(
          'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.VACCINE_CARD.DOWNLOAD'
        );
      case AppointmentStatus.SCHEDULED_PCL:
        return t(
          'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.VACCINE_CARD.UPCOMING'
        );
      default:
        return status;
    }
  };

  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );

  const buttonSize = isSmallScreen ? 'medium' : 'large';
  const {enqueueSnackbar} = useSnackbar();

  const handleCancelAppointment = async (): Promise<void> => {
    if (!appointmentObj?.id || cancelling) {
      return;
    }
    setCancelling(true);
    try {
      await cancelScheduledAppointment(appointmentObj.id);
      enqueueSnackbar(
        t('PAYMENTS_LOGIN.DASHBOARD.CANCEL_APPOINTMENT_SUCCESS'),
        SnackBarConfig('s')
      );
      setCancelDialogOpen(false);
      onAppointmentCancelled?.();
    } catch (err: any) {
      enqueueSnackbar(err?.message || err, SnackBarConfig('e'));
    } finally {
      setCancelling(false);
    }
  };

  const downloadVaccineDetails = async (): Promise<any> => {
    if (!clinicId) return;
    try {
      const blobRes = await downloadVaccineRecord(appointmentObj.id, {
        clinic_id: clinicId
      });
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(
        new Blob([blobRes], {type: 'application/pdf'})
      );
      const firstName = appointmentObj?.patient?.first_name || '';
      const lastName = appointmentObj?.patient?.last_name || '';
      const formattedDate = moment(
        appointmentObj?.appointment_at || new Date()
      ).format('YYYY-MM-DD');
      // file name format vaccine-record-firstname-lastname-YYYY-MM-DD
      const fileName = `vaccine-record-${firstName}-${lastName}-${formattedDate}`;
      downloadLink.download = fileName;

      // Append the link to the body and click it
      document.body.appendChild(downloadLink);
      downloadLink.click();

      // Clean up
      URL.revokeObjectURL(downloadLink.href);
      document.body.removeChild(downloadLink);
    } catch (err: any) {
      enqueueSnackbar(
        err?.message
          ? err.message
          : t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.ERROR_GETTING_VACCINE_LIST'
            ),
        SnackBarConfig('e')
      );
    }
  };

  return (
    <div className="vaccine-cards-view">
      <div className={'card-main-view'}>
        {showPatientName && (
          <span className="vaccine-patient-name">
            {HelperUtils.formFullName(
              appointmentObj?.patient?.first_name,
              appointmentObj?.patient?.last_name,
              appointmentObj?.patient?.middle_name
            )}
          </span>
        )}
        <div
          className={classes.extraLabelStyles}
          style={{display: 'flex', flexDirection: 'column'}}>
          {/* Show the warning text which says insurance cards and consent form is not filled,
            when the vaccine appointment is scheduled from the practice admin */}
          {!isVaccineAdministered &&
            appointmentObj?.created_by !== -11 &&
            appointmentObj?.modified_by != -11 && (
              <span className={classes.warningtText}>
                {t(
                  'PAYMENTS_LOGIN.DASHBOARD.APPOINTMENTS_SCHEULED_WARNING_TEXT'
                )}
              </span>
            )}
          <div className={'label-wrapper'}>
            <div>
              <LabelValue
                label={
                  isVaccineAdministered
                    ? t(
                        'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.VACCINE_CARD.VACCINATION_DATE'
                      )
                    : t(
                        'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.VACCINE_CARD.SCHEDULED_DATE'
                      )
                }
                value={formattedAppointmentDate}
              />
            </div>

            {shouldShowAppointmentType() && (
              <div>
                <LabelValue
                  label={t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.VACCINE_CARD.APPOINTMENT_TYPE'
                  )}
                  value={appointmentObj?.appointment?.label}
                />
              </div>
            )}

            {shouldShowVaccine() && (
              <div>
                <LabelValue
                  label={t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.VACCINE_CARD.VACCINE'
                  )}
                  value={appointmentObj?.vaccine?.proprietary_name}
                />
              </div>
            )}
            {appointmentObj?.vaccine_dose_number === 1 ||
            appointmentObj?.vaccine_dose_number === 2 ? (
              <div>
                <LabelValue
                  label={t('APPOINTMENTS.VACCINE_DOSE')}
                  value={
                    appointmentObj?.vaccine_dose_number === 2
                      ? t('APPOINTMENTS.VACCINE_DOSE_SECOND')
                      : t('APPOINTMENTS.VACCINE_DOSE_FIRST')
                  }
                />
              </div>
            ) : null}
          </div>
        </div>
        <div
          className={
            isSmallScreen
              ? 'edit-and-vaccine-status-buttons-for-small-screen'
              : 'edit-and-vaccine-status-buttons'
          }>
          {isVaccineScheduled &&
            appointmentObj?.appointment_at >= new Date().toISOString() && (
              <>
                <DropDownFieldValue
                  value={
                    <p style={{whiteSpace: 'nowrap'}}>
                      {t(
                        'PAYMENTS_LOGIN.DASHBOARD.EDIT_APPOINTMENT_BUTTON_LABEL'
                      )}
                    </p>
                  }
                  onClickAction={() =>
                    navigateToAppointmentScheduling(appointmentObj)
                  }
                  valueStyles={classes.LinkStyle}
                />
                <DropDownFieldValue
                  value={
                    <p style={{whiteSpace: 'nowrap'}}>
                      {t(
                        'PAYMENTS_LOGIN.DASHBOARD.CANCEL_APPOINTMENT_BUTTON_LABEL'
                      )}
                    </p>
                  }
                  onClickAction={() => setCancelDialogOpen(true)}
                  valueStyles={classes.LinkStyle}
                />
              </>
            )}
          <Button
            onClick={downloadVaccineDetails}
            size={buttonSize}
            disabled={!isVaccineAdministered}
            variant={'outlined'}
            color="primary">
            {getButtonLabel(appointmentObj?.status ?? '')}
          </Button>
        </div>
      </div>
      <ConfirmationDialog
        open={cancelDialogOpen}
        title={t('PAYMENTS_LOGIN.DASHBOARD.CANCEL_APPOINTMENT_CONFIRM_TITLE')}
        description={t('PAYMENTS_LOGIN.DASHBOARD.CANCEL_APPOINTMENT_CONFIRM')}
        confirmButtonText={t(
          'PAYMENTS_LOGIN.DASHBOARD.CANCEL_APPOINTMENT_BUTTON_LABEL'
        )}
        cancelButtonText={t('COMMON.CLOSE')}
        onClose={response => {
          if (response === 'confirm') {
            void handleCancelAppointment();
            return;
          }
          if (!cancelling) {
            setCancelDialogOpen(false);
          }
        }}
        loader={cancelling}
      />
    </div>
  );
};

export default VaccineCard;
